/**
 * AR Technical Demo — derive "disputed" line items from the seeded deductions.
 *
 * THROWAWAY DEMO CODE. The real remittance match data stays clean (every line
 * is `matched`); which lines are *disputed* is a purely presentational, demo-only
 * concept driven off the fixture `deductions` slice. The remittance line-items
 * table badges these rows "Disputed" instead of "Confirmed", and the Flow-3
 * reconciliation view tags them "(short-paid)". Keeping this out of `matchStatus`
 * avoids touching the shared remittance enum / real components. Delete with
 * src/demo/ar.
 */

import { useMemo } from 'react';
import { isArDemoMode } from './demo-mode';
import { useArDemoStore } from './fixture-store';

const EMPTY: ReadonlySet<string> = new Set();

/** Invoice ids that carry a demo deduction — i.e. are disputed. */
export function useDemoDisputedInvoiceIds(): ReadonlySet<string> {
  const deductions = useArDemoStore((s) => s.deductions);
  return useMemo(() => {
    if (!isArDemoMode()) return EMPTY;
    return new Set(deductions.map((d) => d.invoiceId));
  }, [deductions]);
}

/** Cent-accurate "is A strictly less than B" for decimal-string money values. */
function isShortPaid(
  statedAmount: string | null,
  invoiceNet: string | null,
): boolean {
  if (statedAmount == null || invoiceNet == null) return false;
  const paid = Math.round(parseFloat(statedAmount) * 100);
  const owed = Math.round(parseFloat(invoiceNet) * 100);
  if (Number.isNaN(paid) || Number.isNaN(owed)) return false;
  return paid < owed;
}

/**
 * Line-item ids to badge "Disputed" on the remittance screen — the lines that
 * are genuinely short against their OWN invoice (stated payment < invoice net),
 * and only once the agent has processed the remittance (Scene 2) so the cold-
 * open / raw advice shows no dispute badges.
 *
 * Deliberately driven off the line itself, NOT the seeded `deductions` slice: a
 * free-floating chargeback line (no `invoiceId`) names no invoice on the advice,
 * so at this screen we can't attribute it to an invoice yet — it stays
 * "Unmatched", and a fully-paid invoice line stays "Confirmed" even though a
 * deduction is floating. (The Flow-3 reconciliation view still uses
 * `useDemoDisputedInvoiceIds`, which knows the post-portal attribution.)
 */
export function useDemoDisputedLineItemIds(
  lineItems: ReadonlyArray<{
    id: string;
    statedAmount: string | null;
    invoice: { netAmount: string | null } | null;
  }>,
): ReadonlySet<string> {
  const processed = useArDemoStore((s) => s.technicalUi.remittanceProcessed);
  return useMemo(() => {
    if (!isArDemoMode() || !processed) return EMPTY;
    return new Set(
      lineItems
        .filter((li) =>
          isShortPaid(li.statedAmount, li.invoice?.netAmount ?? null),
        )
        .map((li) => li.id),
    );
  }, [processed, lineItems]);
}
